<template>
  <section id="payment-result">
    <div class="result-container">
      <div v-if="loading" class="result-card">
        <b-spinner variant="primary" />
        <p class="mt-3 mb-0">در حال دریافت نتیجهٔ پرداخت...</p>
      </div>

      <div v-else-if="tx" class="result-card" :class="kind">
        <div class="result-icon">
          <v-icon size="44" :color="iconColor">{{ icon }}</v-icon>
        </div>
        <h2>{{ title }}</h2>
        <p class="result-message">{{ subtitle }}</p>

        <div class="result-details">
          <template v-if="tx.purpose === 'renew'">
            <div v-for="item in tx.items" :key="item.imei" class="detail-item">
              <span class="label">{{ item.title || item.imei }}</span>
              <span v-if="item.done" class="value after">تا {{ fa(item.after) }}</span>
              <span v-else-if="tx.status === 'success'" class="value text-warning">در حال تمدید...</span>
              <span v-else class="value text-muted">بدون تغییر</span>
            </div>
          </template>
          <div v-if="tx.gatewayAmount > 0" class="detail-item">
            <span class="label">{{ tx.purpose === 'topup' ? 'مبلغ شارژ' : 'مبلغ کارت' }}</span>
            <span class="value">{{ toman(tx.gatewayAmount) }} تومان</span>
          </div>
          <div v-if="tx.walletAmount > 0" class="detail-item">
            <span class="label">از کیف پول</span>
            <span class="value">
              {{ toman(tx.walletAmount) }} تومان
              <b-badge v-if="tx.walletRefunded" variant="success" pill class="mr-1">به کیف پول برگشت</b-badge>
            </span>
          </div>
          <div v-if="tx.refNumber" class="detail-item">
            <span class="label">شماره مرجع بانک</span>
            <span class="value ltr">{{ tx.refNumber }}</span>
          </div>
          <div class="detail-item">
            <span class="label">کد پیگیری</span>
            <span class="value ltr">{{ tx.ref }}</span>
          </div>
          <div class="detail-item">
            <span class="label">موجودی کیف پول</span>
            <span class="value">{{ toman(walletBalance) }} تومان</span>
          </div>
          <div v-if="tx.status === 'cancelled' || tx.status === 'failed'" class="detail-item">
            <span class="label">اگر از حسابتان کم شده</span>
            <span class="value text-muted">تا ۷۲ ساعت برمی‌گردد</span>
          </div>
        </div>

        <div class="result-actions">
          <template v-if="kind === 'success'">
            <b-button variant="primary" size="lg" @click="goHome">بازگشت به نقشه</b-button>
            <b-button variant="outline-primary" size="lg" @click="$router.push({ name: tx.purpose === 'topup' ? 'subscription-renew' : 'payment-transactions' })">
              {{ tx.purpose === 'topup' ? 'تمدید با کیف پول' : 'تراکنش‌های من' }}
            </b-button>
          </template>
          <template v-else-if="kind === 'failed'">
            <b-button variant="primary" size="lg" @click="retry">تلاش دوباره</b-button>
            <b-button variant="outline-secondary" size="lg" @click="goHome">بازگشت به خانه</b-button>
          </template>
          <template v-else>
            <b-button variant="primary" size="lg" @click="load">به‌روزرسانی</b-button>
            <b-button variant="outline-secondary" size="lg" @click="goHome">بازگشت به خانه</b-button>
          </template>
        </div>
      </div>

      <div v-else class="result-card error">
        <div class="result-icon">
          <v-icon size="44" color="#d97706">mdi-alert-circle</v-icon>
        </div>
        <h2>نتیجه پیدا نشد</h2>
        <p class="result-message">{{ error || 'اگر پرداخت کرده‌اید، نتیجه را در «تراکنش‌های من» ببینید.' }}</p>
        <div class="result-actions">
          <b-button variant="primary" size="lg" @click="$router.push({ name: 'payment-transactions' })">تراکنش‌های من</b-button>
          <b-button variant="outline-secondary" size="lg" @click="goHome">بازگشت به خانه</b-button>
        </div>
      </div>

      <div class="support-info">
        <v-icon size="20" class="ml-2">mdi-headset</v-icon>
        <span>در صورت بروز مشکل با پشتیبانی تماس بگیرید و کد پیگیری را بگویید</span>
      </div>
    </div>
  </section>
</template>

<script>
import { BButton, BBadge, BSpinner } from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'

export default {
  components: { BButton, BBadge, BSpinner },
  data() {
    return {
      loading: true,
      tx: null,
      walletBalance: 0,
      error: '',
      polls: 0,
      timer: null,
    }
  },
  computed: {
    kind() {
      const s = this.tx && this.tx.status
      if (s === 'success') return 'success'
      if (s === 'failed' || s === 'cancelled' || s === 'expired') return 'failed'
      return 'pending'
    },
    icon() {
      return { success: 'mdi-check-bold', failed: 'mdi-close-thick', pending: 'mdi-clock-outline' }[this.kind]
    },
    iconColor() {
      return { success: '#16a34a', failed: '#dc2626', pending: '#d97706' }[this.kind]
    },
    title() {
      if (this.kind === 'success') return this.tx.purpose === 'topup' ? 'کیف پول شارژ شد' : 'پرداخت موفق بود'
      if (this.kind === 'failed') return this.tx.status === 'cancelled' ? 'پرداخت لغو شد' : 'پرداخت انجام نشد'
      return 'در انتظار تأیید بانک'
    },
    subtitle() {
      if (this.kind === 'success' && this.tx.purpose === 'topup') return 'حالا می‌توانید اشتراک را با یک کلیک از کیف پول تمدید کنید.'
      if (this.kind === 'success' && this.tx.purpose === 'renew') {
        const done = this.tx.items.filter(i => i.done).length
        return done === this.tx.items.length ? `اشتراک ${this.fa(done)} دستگاه تمدید شد` : 'پرداخت ثبت شد؛ تمدید در حال انجام است'
      }
      return this.tx.message
    },
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    async load() {
      const { ref } = this.$route.query
      if (!ref) {
        this.loading = false
        return
      }
      try {
        const res = await PaymentService.getResult({ ref })
        if (res.status !== 'success') throw new Error(res.message)
        this.tx = res.result
        this.walletBalance = res.result.walletBalance || 0
        // تا وقتی بانک یا تمدید کارش تمام نشده، چند بار دوباره بپرس
        const waiting = this.kind === 'pending' || (this.tx.status === 'success' && this.tx.purpose === 'renew' && this.tx.renewalStatus !== 'done')
        if (waiting && this.polls < 10) {
          this.polls += 1
          this.timer = setTimeout(this.load, 3000)
        }
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    toman(rial) {
      return new Intl.NumberFormat('fa-IR').format(Math.round((rial || 0) / 10))
    },
    fa(value) {
      return String(value || '').replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    goHome() {
      this.$router.push({ name: 'home' })
    },
    retry() {
      this.$router.push({ name: this.tx && this.tx.purpose === 'topup' ? 'payment-wallet' : 'subscription-renew' })
    },
  },
}
</script>

<style scoped>
#payment-result {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px;
}

.result-container {
  max-width: 480px;
  width: 100%;
}

.result-card {
  background: white;
  border-radius: 22px;
  padding: 32px 26px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.1);
}

.result-icon {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  background: #fef3c7;
}

.result-card.success .result-icon {
  background: #dcfce7;
}

.result-card.failed .result-icon {
  background: #fee2e2;
}

.result-card h2 {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 6px;
}

.result-message {
  color: #64748b;
  margin-bottom: 18px;
}

.result-details {
  background: #f8fafc;
  border-radius: 14px;
  padding: 6px 16px;
  margin-bottom: 20px;
  text-align: right;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.detail-item:last-child {
  border-bottom: 0;
}

.detail-item .label {
  color: #64748b;
  flex: none;
}

.detail-item .value {
  font-weight: 700;
  color: #0f172a;
  text-align: left;
}

.after {
  color: #16a34a !important;
}

.ltr {
  direction: ltr;
  unicode-bidi: isolate;
}

.result-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.support-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  color: #64748b;
  font-size: 13px;
  text-align: center;
}
</style>
